import { SelectChangeEvent } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { ReactNode, useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import { DropdownListItem } from '@verantos-dev/shared-components';

import {
  useGetHolidaysDataQuery,
  useGetMetricBatchTypeListQuery,
  useGetSummaryDateRangeQuery,
} from './api/metricsApiSlice';
import {
  clearSummaryEndDate,
  clearSummaryStartDate,
  setSummaryBatchTypeId,
  setSummaryEndDate,
  setSummaryStartDate,
} from './state/metricsSlice';
import {
  selectSelectedProjectId,
  selectSummaryBatchTypeId,
  selectSummaryEndDate,
  selectSummaryStartDate,
} from './state/selector';
import { SummaryDateRange } from './types/responseTypes';

type DropdownChangeHandler = (
  event: SelectChangeEvent<unknown> | (Event & { target: { value: unknown; name: string } }),
  child: ReactNode
) => void;

interface UseMetricSummaryResults {
  summaryDateRange: SummaryDateRange | undefined;
  isSummaryDateRangeLoading: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  handleChangeStartDate: (val: Dayjs | null) => void;
  handleChangeEndDate: (val: Dayjs | null) => void;
  batchTypeOptions: DropdownListItem[];
  batchTypeId: string | null;
  isMetricsBatchTypeListLoading: boolean;
  handleBatchTypeChange: DropdownChangeHandler;
  projectId: string | null;
  shouldDisableStartDate: (day: Dayjs) => boolean;
}

export const useMetricSummary = (): UseMetricSummaryResults => {
  const projectId = useAppSelector(selectSelectedProjectId);
  const batchTypeId = useAppSelector(selectSummaryBatchTypeId);
  const startDate = useAppSelector(selectSummaryStartDate);
  const endDate = useAppSelector(selectSummaryEndDate);
  const {
    data: summaryDateRangeData,
    isLoading: isSummaryDateRangeLoading,
    isFetching: isSummaryDateRangeFetching,
  } = useGetSummaryDateRangeQuery(
    {
      projectId: projectId || '',
      batchTypeId: batchTypeId || '',
    },
    {
      skip: !(projectId && batchTypeId),
      refetchOnMountOrArgChange: true,
    }
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (isSummaryDateRangeFetching) return;
    if (!summaryDateRangeData?.data?.minDate || !summaryDateRangeData?.data?.maxDate) return;
    dispatch(setSummaryStartDate(dayjs(summaryDateRangeData.data?.minDate).toISOString()));
    dispatch(setSummaryEndDate(dayjs(summaryDateRangeData.data?.maxDate).toISOString()));
  }, [
    summaryDateRangeData?.data?.minDate,
    summaryDateRangeData?.data?.maxDate,
    isSummaryDateRangeFetching,
    dispatch,
  ]);

  const handleChangeStartDate = (val: Dayjs | null) => {
    if (val) {
      dispatch(setSummaryStartDate(val.toISOString()));
    }
  };
  const handleChangeEndDate = (val: Dayjs | null) => {
    if (val) {
      dispatch(setSummaryEndDate(val.toISOString()));
    }
  };

  const {
    data: metricsBatchTypeListData,
    isLoading: isMetricsBatchTypeListLoading,
    isFetching: isMetricsBatchTypeListFetching,
  } = useGetMetricBatchTypeListQuery({ projectId: projectId ?? '' }, { skip: !projectId });

  const batchTypeOptions: DropdownListItem[] = useMemo(() => {
    if (!metricsBatchTypeListData?.data || metricsBatchTypeListData.data.length === 0) {
      return [];
    }
    return metricsBatchTypeListData.data.map((batchType) => ({
      value: String(batchType.id),
      displayValue: batchType.label,
    }));
  }, [metricsBatchTypeListData]);

  const handleBatchTypeChange: DropdownChangeHandler = (event) => {
    const value = event.target?.value;
    if (typeof value === 'string') {
      dispatch(setSummaryBatchTypeId(value));
    }
  };

  const { data: holidaysData } = useGetHolidaysDataQuery();

  const holidayDates = useMemo<Set<string>>(() => {
    if (!holidaysData?.data) return new Set();
    return new Set(holidaysData.data.map((h) => dayjs(h.date).format('YYYY-MM-DD')));
  }, [holidaysData]);

  const shouldDisableStartDate = (day: Dayjs) => {
    const dow = day.day();
    if (dow === 0 || dow === 6) return true;
    return holidayDates.has(day.format('YYYY-MM-DD'));
  };

  return {
    summaryDateRange: summaryDateRangeData?.data,
    isSummaryDateRangeLoading: isSummaryDateRangeFetching || isSummaryDateRangeLoading,
    startDate: startDate ? dayjs(startDate) : null,
    endDate: endDate ? dayjs(endDate) : null,

    handleChangeStartDate,
    handleChangeEndDate,
    batchTypeOptions,
    batchTypeId,
    isMetricsBatchTypeListLoading: isMetricsBatchTypeListFetching || isMetricsBatchTypeListLoading,
    handleBatchTypeChange,
    projectId,
    shouldDisableStartDate,
  };
};
